import { collection, doc, getDocs, query, where, addDoc, updateDoc, deleteDoc, orderBy, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { compressImage } from './misc';
import type {
  ContainerVan,
  ContainerVanChecklistTemplate,
  ContainerVanChecklistCategory,
  ContainerVanChecklistData
} from '../types/containerVan';

/**
 * Get Firebase Storage download URL from storage path
 */
export const getStorageUrl = async (storagePath: string): Promise<string> => {
  try {
    const storageRef = ref(storage, storagePath);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error('Error getting storage URL:', error);
    throw error;
  }
};

/**
 * Convert color-based status to descriptive status for Firestore
 */
export const convertColorStatusToDescriptive = (colorStatus: 'green' | 'yellow' | 'red'): 'pass' | 'warning' | 'fail' => {
  switch (colorStatus) {
    case 'green':
      return 'pass';
    case 'yellow':
      return 'warning';
    case 'red':
      return 'fail';
    default:
      return 'pass'; // Default fallback
  }
};

/**
 * Convert descriptive status to color-based status for UI
 */
export const convertDescriptiveStatusToColor = (descriptiveStatus: 'pass' | 'warning' | 'fail'): 'green' | 'yellow' | 'red' => {
  switch (descriptiveStatus) {
    case 'pass':
      return 'green';
    case 'warning':
      return 'yellow';
    case 'fail':
      return 'red';
    default:
      return 'green'; // Default fallback
  }
};

// Interface for PDF generation parameters
export interface ContainerVanPDFGenerationParams {
  vanNumber: string;
  genSetNumber: string;
  location: string;
  companyName: string;
  categories: ContainerVanChecklistCategory[];
  totalRequired: number;
  completedRequired: number;
  isFullyComplete: boolean;
  overallStatus?: 'pass' | 'warning' | 'fail' | 'green' | 'yellow' | 'red';
  inspectionSummary?: string;
  signatureDataURL?: string;
  inspectorName?: string;
  checklistType?: string;
  genSetRemainingHours?: number;
}

/**
 * Search container vans by van number
 */
export const searchContainerVansByNumber = async (
  vanNumber: string,
  companyId?: string
): Promise<ContainerVan[]> => {
  try {
    console.log('Searching with vanNumber:', vanNumber, 'companyId:', companyId);
    
    const containerVansRef = collection(db, 'containerVanMasterlist');
    let q = query(containerVansRef);

    // Add company filter if provided
    if (companyId) {
      q = query(q, where('companyId', '==', companyId));
    }

    // Add van number filter
    if (vanNumber.trim()) {
      // For exact match first
      const exactQuery = query(q, where('vanNumber', '==', vanNumber.trim()));
      const exactSnapshot = await getDocs(exactQuery);
      
      console.log('Exact match results:', exactSnapshot.size);
      
      if (!exactSnapshot.empty) {
        const results = exactSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as ContainerVan));
        console.log('Exact match found:', results);
        return results;
      }

      // If no exact match, try partial match
      const partialQuery = query(
        q,
        where('vanNumber', '>=', vanNumber.trim()),
        where('vanNumber', '<=', vanNumber.trim() + '\uf8ff'),
        orderBy('vanNumber')
      );
      const partialSnapshot = await getDocs(partialQuery);
      
      console.log('Partial match results:', partialSnapshot.size);
      
      const results = partialSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ContainerVan));
      
      console.log('Partial match found:', results);
      return results;
    }

    return [];
  } catch (error) {
    console.error('Error searching container vans:', error);
    throw error;
  }
};

/**
 * Get container van checklist template
 */
export const getContainerVanChecklistTemplate = async (): Promise<ContainerVanChecklistTemplate | null> => {
  try {
    // Note: Collection name has typo in the script, but using as provided
    const templateSnapshot = await getDocs(query(collection(db, 'containerVanCheklistTemplate'), where('id', '==', 'default')));
    
    if (!templateSnapshot.empty) {
      const templateDoc = templateSnapshot.docs[0];
      return {
        id: templateDoc.id,
        ...templateDoc.data()
      } as ContainerVanChecklistTemplate;
    }

    return null;
  } catch (error) {
    console.error('Error getting container van checklist template:', error);
    throw error;
  }
};

/**
 * Save container van checklist to Firestore
 */
export const saveContainerVanChecklist = async (
  checklistData: Omit<ContainerVanChecklistData, 'id'>
): Promise<string> => {
  try {
    const checklistsRef = collection(db, 'containerVanChecklist');
    const docRef = await addDoc(checklistsRef, {
      ...checklistData,
      created_at: Timestamp.now(),
      updated_at: Timestamp.now()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving container van checklist:', error);
    throw error;
  }
};

/**
 * Update container van checklist
 */
export const updateContainerVanChecklist = async (
  checklistId: string,
  updateData: Partial<ContainerVanChecklistData>
): Promise<void> => {
  try {
    const checklistRef = doc(db, 'containerVanChecklist', checklistId);
    await updateDoc(checklistRef, {
      ...updateData,
      updated_at: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating container van checklist:', error);
    throw error;
  }
};

/**
 * Get container van checklists for monitoring with optional date filtering
 */
export const getContainerVanChecklists = async (
  companyId: string,
  createdFrom?: string,
  createdTo?: string
): Promise<ContainerVanChecklistData[]> => {
  try {
    const checklistsRef = collection(db, 'containerVanChecklist');
    
    // Build query conditions
    const queryConditions = [where('companyId', '==', companyId)];
    
    // Add date range filters if provided
    if (createdFrom) {
      const fromDate = new Date(createdFrom);
      fromDate.setHours(0, 0, 0, 0);
      queryConditions.push(where('created_at', '>=', Timestamp.fromDate(fromDate)));
    }
    
    if (createdTo) {
      const toDate = new Date(createdTo);
      toDate.setHours(23, 59, 59, 999);
      queryConditions.push(where('created_at', '<=', Timestamp.fromDate(toDate)));
    }
    
    const q = query(
      checklistsRef,
      ...queryConditions,
      orderBy('created_at', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ContainerVanChecklistData));
  } catch (error) {
    console.error('Error getting container van checklists:', error);
    throw error;
  }
};

/**
 * Calculate checklist statistics
 */
export const calculateContainerVanChecklistStats = (categories: ContainerVanChecklistCategory[]) => {
  let totalItems = 0;
  let completedItems = 0;
  let passedItems = 0;
  let failedItems = 0;

  categories.forEach(category => {
    category.items.forEach(item => {
      totalItems++;
      if (item.value !== undefined && item.value !== null && item.value !== '') {
        completedItems++;
        
        if (item.type === 'checkbox' && item.value === true) {
          passedItems++;
        } else if (item.type === 'slider' && typeof item.value === 'number') {
          passedItems++;
        } else if (item.value && item.value !== '') {
          passedItems++;
        }
      }
    });
  });

  const completionPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  const passPercentage = totalItems > 0 ? Math.round((passedItems / totalItems) * 100) : 0;

  return {
    totalItems,
    completedItems,
    passedItems,
    failedItems,
    completionPercentage,
    passPercentage,
    isComplete: completedItems === totalItems
  };
};

/**
 * Delete container van checklist
 */
export const deleteContainerVanChecklist = async (checklistId: string): Promise<void> => {
  try {
    const checklistRef = doc(db, 'containerVanChecklist', checklistId);
    await deleteDoc(checklistRef);
  } catch (error) {
    console.error('Error deleting container van checklist:', error);
    throw error;
  }
};

/**
 * Generate PDF summary for container van checklists
 */
export const generateContainerVanPDFSummary = async (params: ContainerVanPDFGenerationParams): Promise<string> => {
  try {
    // Dynamic import for better code splitting
    const { jsPDF } = await import('jspdf');
    
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
    const pageWidth = pdf.internal.pageSize.width;
    const margin = 20;
    let yPosition = margin;

    // Helper function to add text with word wrap
    const addText = (text: string, x: number, y: number, maxWidth?: number, fontSize = 10) => {
      pdf.setFontSize(fontSize);
      if (maxWidth) {
        const lines = pdf.splitTextToSize(text, maxWidth);
        pdf.text(lines, x, y);
        return y + (lines.length * fontSize * 0.4);
      } else {
        pdf.text(text, x, y);
        return y + (fontSize * 0.4);
      }
    };

    // Header - keep larger for title
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    yPosition = addText('Container Van Inspection Report', margin, yPosition, undefined, 16) + 10;

    // Company information - normal font
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    yPosition = addText(`Company: ${params.companyName}`, margin, yPosition) + 3;
    yPosition = addText(`Van Number: ${params.vanNumber}`, margin, yPosition) + 3;
    yPosition = addText(`GenSet Number: ${params.genSetNumber}`, margin, yPosition) + 3;
    yPosition = addText(`Location: ${params.location}`, margin, yPosition) + 3;
    
    if (params.inspectorName) {
      yPosition = addText(`Inspector: ${params.inspectorName}`, margin, yPosition) + 3;
    }
    
    yPosition += 5; // Extra space before summary
    
    // Summary - section header (bigger and bold)
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    yPosition = addText('Inspection Summary', margin, yPosition, undefined, 11) + 3;
    
    // Overall Status with text representation - normal content
    let statusText = '';
    switch(params.overallStatus) {
      case 'green':
      case 'pass': 
        statusText = 'Pass'; 
        break;
      case 'yellow':
      case 'warning': 
        statusText = 'Warning'; 
        break; 
      case 'red':
      case 'fail': 
        statusText = 'Failed'; 
        break;
      default: 
        statusText = 'N/A'; 
        break;
    }
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    yPosition = addText(`   Overall Status: ${statusText}`, margin, yPosition) + 2;
    
    // GenSet Remaining Operating Hours - normal content
    if (params.genSetRemainingHours !== undefined) {
      const hoursText = params.genSetRemainingHours > 0 
        ? `${params.genSetRemainingHours.toFixed(1)} hours`
        : 'Unable to calculate';
      yPosition = addText(`   GenSet Remaining Operating Hours: ${hoursText}`, margin, yPosition) + 2;
    }
    
    // Additional Notes (Inspection Summary) - normal content
    if (params.inspectionSummary) {
        yPosition = addText(`   Note: ${params.inspectionSummary}`, margin, yPosition, pageWidth - margin * 2 - 10) + 3;
    }

    yPosition += 5; // Extra space before inspection details

    // Categories - section header (bigger and bold)
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    yPosition = addText('Inspection Details', margin, yPosition, undefined, 11) + 3;

    params.categories.forEach((category) => {
      // More aggressive page check - only add page if we really need it
      if (yPosition > 240) {
        pdf.addPage();
        yPosition = margin;
      }

      // Category header - normal font size with underline
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const categoryText = category.name.toUpperCase();
      yPosition = addText(`   ${categoryText}`, margin, yPosition, undefined, 10) + 1;
      
      // Add underline for category
      // const textWidth = pdf.getTextWidth(categoryText);
      // pdf.line(margin, yPosition, margin + textWidth, yPosition);
      // yPosition += 3;

      category.items.forEach((item) => {
        if (yPosition > 260) {
          pdf.addPage();
          yPosition = margin;
        }

        const itemText = `  • ${item.text}`;
        let valueText = '';

        if (item.type === 'checkbox') {
          valueText = item.value ? '✓ Pass' : '✗ Fail';
        } else if (item.type === 'slider' || item.type === 'gauge') {
          valueText = `${item.value || 'N/A'}${item.unit || ''}`;
        } else {
          valueText = item.value ? `${item.value}` : 'N/A';
        }

        // Item text and value - normal font
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        yPosition = addText(`${itemText}: ${valueText}`, margin + 5, yPosition, pageWidth - margin * 2 - 30) + 2;

        if (item.notes) {
          yPosition = addText(`• Notes: ${item.notes}`, margin + 5, yPosition, pageWidth - margin * 2 - 30) + 2;
        }
      });

      yPosition += 3;
      // Add underline for category
      // const textWidth = pdf.getTextWidth(categoryText);
      // pdf.line(margin, yPosition, margin , yPosition);
      // yPosition += 3;
    });

    // Signature - optimized to fit on same page
    if (params.signatureDataURL) {
      // Only add new page if we're really running out of space
      if (yPosition > 230) {
        pdf.addPage();
        yPosition = margin;
      }

      // Section header (bigger and bold)
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      yPosition = addText('Inspector Signature', margin, yPosition, undefined, 11) + 3;
      
      try {
        // Smaller signature to fit better
        pdf.addImage(params.signatureDataURL, 'PNG', margin, yPosition, 40, 20);
        yPosition += 25;
      } catch (error) {
        console.warn('Could not add signature to PDF:', error);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        yPosition = addText('Signature could not be embedded', margin, yPosition) + 5;
      }
      
      // Add Inspector and Inspected Date/Time below signature - normal font
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      if (params.inspectorName) {
        yPosition = addText(`Inspector: ${params.inspectorName}`, margin, yPosition) + 2;
      }
      yPosition = addText(`Inspected Date/Time: ${new Date().toLocaleString()}`, margin, yPosition) + 5;
    }

    // Generate filename and save
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `container-van-inspection-${params.vanNumber}-${timestamp}.pdf`;
    
    pdf.save(filename);
    return filename;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

interface FuelEstimate {
  remainingLiters: number;
  hoursRemaining: number;
}

export const computeFuelEstimate = (
  capacityLiters: number,
  consumptionRateLitersPerHour: number,
  remainingPercent: number
): FuelEstimate => {
  const percentage = Math.max(0, Math.min(remainingPercent, 100));

  const remainingLiters = (percentage / 100) * capacityLiters;

  const hoursRemaining =
    consumptionRateLitersPerHour > 0
      ? remainingLiters / consumptionRateLitersPerHour
      : Infinity;

  return {
    remainingLiters,
    hoursRemaining,
  };
};

/**
 * Upload category photo to Firebase Storage
 * @param file - The image file to upload
 * @param companyId - Company ID for organizing files
 * @param categoryId - Category ID for organizing files
 * @param vanNumber - Van number for organizing files
 * @returns Promise<string> - The storage path of the uploaded image
 */
export const uploadCategoryPhoto = async (
  file: File,
  companyId: string,
  categoryId: string,
  vanNumber: string
): Promise<string> => {
  try {
    // Compress the image before upload
    const compressedFile = await compressImage(file, 800, 600, 0.8);
    
    // Create file path: containervan/{companyId}/checklist_images/{date}/{vanNumber}_{categoryId}_{timestamp}.jpg
    const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const timestamp = Date.now();
    const fileExtension = compressedFile.type.split('/')[1] || 'jpg';
    const fileName = `${vanNumber}_${categoryId}_${timestamp}.${fileExtension}`;
    const filePath = `containervan/${companyId}/checklist_images/${date}/${fileName}`;
    
    // Create storage reference
    const storageRef = ref(storage, filePath);
    
    // Upload file
    await uploadBytes(storageRef, compressedFile);
    
    // Return storage path instead of download URL
    return filePath;
  } catch (error) {
    console.error('Error uploading category photo:', error);
    throw new Error('Failed to upload photo');
  }
};

/**
 * Upload multiple category photos
 * @param photoData - Array of photo upload data
 * @returns Promise<string[]> - Array of storage paths
 */
export const uploadCategoryPhotos = async (
  photoData: Array<{
    file: File;
    companyId: string;
    categoryId: string;
    vanNumber: string;
  }>
): Promise<string[]> => {
  try {
    const uploadPromises = photoData.map(data =>
      uploadCategoryPhoto(data.file, data.companyId, data.categoryId, data.vanNumber)
    );
    
    const storagePaths = await Promise.all(uploadPromises);
    return storagePaths;
  } catch (error) {
    console.error('Error uploading category photos:', error);
    throw new Error('Failed to upload photos');
  }
};

  // Get status badge color
  export const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass': return 'green';
      case 'green': return 'green';
      case 'yellow': return 'yellow';
      case 'fail': return 'red';
      case 'red': return 'red';
      default: return 'gray';
    }
  };